// Panelmalen — ÉN mal for hvert panel på flata.
//
// Samme mal som COP (`apps/cop/src/ui/windows.ts`), med de samme reglene fra
// UX_DESIGNSYSTEM §4:
//   * ett standardisert header-kluster: `⤢` (pop-out) + `×` (lukk), samme sted
//     på hvert panel
//   * drag KUN i en EKTE tittellinje, med 5 px terskel så et rent klikk aldri
//     flytter panelet
//   * pop-out er et EKTE `window.open` (egen skjerm), ikke et falskt lag
//
// FORSKJELLEN FRA COP, OG HVORFOR: COP-panelene flyter over kartet fra starten.
// En sensorflate er ÉN operatørs arbeidsflate, og der er et fast rutenett bedre:
// sensorene, kartet, lista og kortet skal ALLTID være der, for operatøren skal
// ikke måtte lete etter hydrofonen (eller den seismiske kjeden) mens noe skjer.
// Panelene er derfor DOKKET som standard, og drag i tittellinja LØSNER panelet
// til et flytende vindu for den som har to skjermer og vil bygge sitt eget
// oppsett. Å ha begge tilstandene i samme mal er billigere enn to systemer, og
// «Sett tilbake»-knappen gjør veien hjem åpenbar.
//
// Pop-out åpner flata på nytt med `?panel=<nøkkel>`, som appens `main.ts` leser
// og booter i frittstående modus. Det er samme kontrakt som COP-ens
// `POPOUT_KEY_BY_CLASS`, bare uttrykt som en URL-parameter i stedet for et kart
// — det er én ting mindre å holde i sync når et panel legges til.
import { div, knapp, span } from './dom.js';
import { ikon } from './ikoner.js';
import { tomt } from './tilstand.js';
/** Terskel i piksler før et pointer-nede blir en flytting. Se kommentar i `dragPosisjon`. */
export const DRAG_TERSKEL = 5;
/**
 * Navnet flata bruker på vinduer den åpner (`window.open(..., '<navn>-<panel>')`).
 *
 * Dette er den ENE strengen i panelmalen som må vite hvilken app den kjører i:
 * to apper fra samme pakke som popper ut panelet «kart» skal ikke overta
 * hverandres vindu. Appen setter den én gang i oppstarten (`settFlatenavn`), og
 * standardverdien er nøytral framfor å være noens navn.
 */
let flatenavn = 'flate';
export function settFlatenavn(navn) {
    flatenavn = navn;
}
/**
 * REN drag-matematikk: hvor havner panelet, og har vi passert terskelen?
 *
 * Skilt ut fra DOM-en av samme grunn som `erSkrivefelt` i COP-ens `windows.ts`:
 * testoppsettet har ingen jsdom, og en regel som ikke kan testes er en regel som
 * stille slutter å gjelde. Og dette ER en regel som har feilet før i dette
 * programmet — panelene «hoppet vekk» ved et rent klikk fordi terskelen manglet.
 *
 * Klemmingen holder alltid minst 80 px av panelet innenfor på hver side, og
 * aldri over toppstripa (40 px). Et panel som er dratt ut av viewporten er i
 * praksis borte, og på en post uten mus (berøringsskjerm med hansker) finnes
 * ingen enkel vei tilbake.
 */
export function dragPosisjon(start, naa, ramme, terskel = DRAG_TERSKEL) {
    const dx = naa.x - start.x;
    const dy = naa.y - start.y;
    const aktiv = Math.abs(dx) >= terskel || Math.abs(dy) >= terskel;
    const SYNLIG = 80;
    const TOPPSTRIPE = 40;
    const left = Math.max(SYNLIG - ramme.panelBredde, Math.min(ramme.vindusBredde - SYNLIG, start.left + dx));
    const top = Math.max(TOPPSTRIPE, Math.min(ramme.vindusHoyde - SYNLIG, start.top + dy));
    return { aktiv, left, top };
}
/**
 * Bygg et panel. Rekkefølgen i tittellinja er fast på hvert panel, fordi
 * musklet minne er den eneste grunnen til at en operatør treffer en 24 px knapp
 * i mørket: grep-hint · tittel · undertekst · (panelets egne kontroller) ·
 * «Sett tilbake» · `⤢` · `×`.
 */
export function lagPanel(valg) {
    const rot = div(`panel panel-${valg.nokkel}`);
    rot.dataset['panel'] = valg.nokkel;
    const hode = div('panel-hode');
    // Tittellinja er drag-håndtaket. Den er en ekte header-node, ikke hele
    // panelet — se §4 og kommentaren i `dragPosisjon`.
    const grep = ikon('grep');
    grep.classList.add('panel-grep');
    hode.append(grep);
    hode.append(span('panel-tittel', valg.tittel));
    const under = span('panel-under', valg.under ?? '');
    hode.append(under);
    const rom = div('panel-rom');
    hode.append(rom);
    const egne = div('panel-egne');
    hode.append(egne);
    const tilbake = knapp('Sett tilbake', () => dokk(), { variant: 'lenke', ikon: ikon('dokk'), tittel: 'Sett panelet tilbake i rutenettet' });
    tilbake.classList.add('panel-dokk');
    tilbake.hidden = true;
    hode.append(tilbake);
    const pop = knapp('', () => popUt(valg.nokkel), { variant: 'lenke', ikon: ikon('popout'), tittel: 'Åpne i eget vindu (egen skjerm)' });
    pop.classList.add('panel-popout');
    hode.append(pop);
    if (valg.kanLukkes !== false) {
        const lukk = knapp('', () => { rot.hidden = true; valg.paaLukk?.(); }, { variant: 'lenke', ikon: ikon('kryss'), tittel: `Lukk ${valg.tittel.toLowerCase()}` });
        lukk.classList.add('panel-x');
        hode.append(lukk);
    }
    const kropp = div('panel-kropp');
    rot.append(hode, kropp);
    if (valg.kanLosnes !== false)
        gjørLosnbar(rot, hode, tilbake);
    function dokk() {
        rot.classList.remove('flytende');
        rot.style.left = '';
        rot.style.top = '';
        rot.style.width = '';
        rot.style.height = '';
        tilbake.hidden = true;
    }
    return {
        rot,
        kropp,
        settUnder(tekst) { under.textContent = tekst; },
        leggIHode(node) { egne.append(node); },
        vis(på) { rot.hidden = !på; },
    };
}
/**
 * Pop-out. Ekte `window.open` med samme dokument og `?panel=<nøkkel>` —
 * ingen falsk overlay som later som den er et vindu (§4). Blir vinduet blokkert
 * av nettleseren, SIER vi det: en knapp som ikke gjorde noe og ikke forklarte
 * hvorfor er nettopp «mockup og tull» (§10).
 */
function popUt(nokkel) {
    const url = new URL(location.href);
    url.searchParams.set('panel', nokkel);
    const w = globalThis.open?.(url.toString(), `${flatenavn}-${nokkel}`, 'width=900,height=760,noopener');
    if (!w) {
        varsle('Nettleseren blokkerte det nye vinduet. Tillat pop-up for denne siden, eller åpne lenken manuelt.');
    }
}
/**
 * Enkel varsellinje nederst. Ligger her og ikke i `tilstand.ts` fordi den må
 * kunne brukes av panelmalen selv, som ikke skal kjenne appens skall.
 */
function varsle(melding) {
    const boks = div('varsellinje', melding);
    boks.setAttribute('role', 'alert');
    document.body.append(boks);
    globalThis.setTimeout(() => boks.remove(), 6000);
}
/** Drag i tittellinja løsner panelet fra rutenettet. Se filens toppkommentar. */
function gjørLosnbar(rot, hode, tilbake) {
    let start = null;
    let aktiv = false;
    let pid = -1;
    hode.addEventListener('pointerdown', (e) => {
        // Aldri dra fra en kontroll — da ville `⤢` og `×` blitt umulige å treffe.
        if (e.target.closest('button, input, select, textarea, a'))
            return;
        const r = rot.getBoundingClientRect();
        start = { x: e.clientX, y: e.clientY, left: r.left, top: r.top };
        aktiv = false;
        pid = e.pointerId;
    });
    hode.addEventListener('pointermove', (e) => {
        if (!start)
            return;
        const r = rot.getBoundingClientRect();
        const p = dragPosisjon(start, { x: e.clientX, y: e.clientY }, {
            vindusBredde: window.innerWidth,
            vindusHoyde: window.innerHeight,
            panelBredde: r.width,
        });
        if (!p.aktiv)
            return;
        if (!aktiv) {
            aktiv = true;
            // Frys størrelsen i det panelet løsner, ellers kollapser det til
            // innholdets bredde i det øyeblikket det forlater rutenettet.
            rot.style.width = `${r.width}px`;
            rot.style.height = `${r.height}px`;
            rot.classList.add('flytende');
            tilbake.hidden = false;
            try {
                hode.setPointerCapture(pid);
            }
            catch { /* eldre nettleser — drag uten capture */ }
        }
        rot.style.left = `${p.left}px`;
        rot.style.top = `${p.top}px`;
        e.preventDefault();
    });
    const slutt = () => { start = null; aktiv = false; };
    hode.addEventListener('pointerup', slutt);
    hode.addEventListener('pointercancel', slutt);
}
/** Hvilket panel er vi poppet ut som? `null` = hele flata. */
export function poppetPanel(søk = location.search) {
    return new URLSearchParams(søk).get('panel');
}
export function montérPoppetPanel(rot, paneler, valg = {}) {
    const nøkkel = poppetPanel(valg.søk ?? location.search);
    if (!nøkkel)
        return 'hele-flata';
    document.body.classList.add('standalone-panel');
    const behold = valg.behold ?? [];
    const valgt = paneler[nøkkel];
    if (!valgt) {
        // Ukjent panelnøkkel i URL-en. Vis en ærlig beskjed framfor et tomt vindu:
        // et blankt vindu leses som «appen er ødelagt», ikke som «feil lenke».
        rot.replaceChildren(...behold, tomt('Ukjent panel', `Nettadressen ber om panelet «${nøkkel}», som ikke finnes i denne flata.`));
        return 'ukjent';
    }
    rot.replaceChildren(...behold, valgt.rot);
    valgt.rot.classList.add('panel-alene');
    return 'panel';
}
//# sourceMappingURL=vindu.js.map